/**
 * Détection de doublons parmi les individus d'un zoo.
 *
 * Heuristiques (par ordre de confiance) :
 *  - GAN identique (identifiant ZIMS, globalement unique)         → confiance élevée
 *  - Numéro de puce identique                                     → confiance élevée
 *  - Nom identique (normalisé) au sein de la même espèce          → confiance élevée
 *  - Noms très proches (Levenshtein) au sein de la même espèce    → à vérifier
 *
 * Deux individus ne sont comparés que s'ils appartiennent au même zoo
 * (`zooId` identique) : un même GAN entre deux zoos correspond au schéma
 * légitime de transfert (fiche `isDeparted` chez A + fiche neuve chez B).
 *
 * Les faux positifs confirmés par l'utilisateur sont mémorisés via le champ
 * additif `notDuplicateOf: [animalId]` sur chacun des documents concernés.
 */

import { formatBirthdate } from "./birthdate";

export const DUPLICATE_MATCH_TYPES = {
  SAME_GAN: "sameGan",
  SAME_CHIP: "sameChip",
  SAME_NAME: "sameName",
  SIMILAR_NAME: "similarName",
};

const HIGH_CONFIDENCE_MATCHES = [
  DUPLICATE_MATCH_TYPES.SAME_GAN,
  DUPLICATE_MATCH_TYPES.SAME_CHIP,
  DUPLICATE_MATCH_TYPES.SAME_NAME,
];

// Valeurs placeholder rencontrées dans la base : ne doivent jamais matcher.
const JUNK_IDENTIFIERS = new Set(["", "N/C", "NC", "N/A", "NA", "NONE", "0", "MA PUCE", "TBD"]);

export const normalizeText = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const normalizeIdentifier = (value) => {
  const cleaned = (value || "").toString().trim().toUpperCase();
  return JUNK_IDENTIFIERS.has(cleaned) ? "" : cleaned;
};

export const normalizeGan = (gan) => normalizeIdentifier(gan);

export const normalizeChip = (chip) => {
  const cleaned = normalizeIdentifier(chip);
  // Les transpondeurs réels font au moins une dizaine de caractères ;
  // en dessous de 4 on considère la valeur comme non significative.
  return cleaned.length < 4 ? "" : cleaned;
};

export const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (v, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + substitutionCost
      );
    }
    previous = current;
  }
  return previous[b.length];
};

const namesAreSimilar = (nameA, nameB) => {
  if (nameA.length < 4 || nameB.length < 4) return false;
  const maxDistance = Math.min(nameA.length, nameB.length) <= 6 ? 1 : 2;
  return levenshtein(nameA, nameB) <= maxDistance;
};

const pairKey = (idA, idB) => (idA < idB ? `${idA}|${idB}` : `${idB}|${idA}`);

const isPairDismissed = (animalA, animalB) =>
  (animalA.notDuplicateOf || []).includes(animalB.animalId) ||
  (animalB.notDuplicateOf || []).includes(animalA.animalId);

const canCompare = (animalA, animalB) =>
  animalA.zooId === animalB.zooId && !isPairDismissed(animalA, animalB);

/**
 * Détecte les groupes de doublons potentiels.
 *
 * @param {Array} animals — liste des documents animaux (non archivés)
 * @returns {Array<{ animals: Array, matchTypes: string[], confidence: 'high'|'medium' }>}
 */
export const findDuplicateGroups = (animals) => {
  const list = (animals || []).filter((a) => a && a.animalId);
  const byId = new Map(list.map((a) => [a.animalId, a]));

  // pairKey → Set(matchTypes)
  const pairs = new Map();
  const addPair = (animalA, animalB, matchType) => {
    if (!canCompare(animalA, animalB)) return;
    const key = pairKey(animalA.animalId, animalB.animalId);
    if (!pairs.has(key)) pairs.set(key, new Set());
    pairs.get(key).add(matchType);
  };

  const addExactMatches = (getKey, matchType) => {
    const buckets = new Map();
    list.forEach((animal) => {
      const key = getKey(animal);
      if (!key) return;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(animal);
    });
    buckets.forEach((bucket) => {
      for (let i = 0; i < bucket.length; i++) {
        for (let j = i + 1; j < bucket.length; j++) {
          addPair(bucket[i], bucket[j], matchType);
        }
      }
    });
  };

  addExactMatches((a) => normalizeGan(a.animalGan), DUPLICATE_MATCH_TYPES.SAME_GAN);
  addExactMatches((a) => normalizeChip(a.animalChipId), DUPLICATE_MATCH_TYPES.SAME_CHIP);

  // Comparaison des noms au sein de chaque espèce
  const bySpecies = new Map();
  list.forEach((animal) => {
    if (!animal.specieId) return;
    const name = normalizeText(animal.animalName);
    if (name.length < 2) return;
    if (!bySpecies.has(animal.specieId)) bySpecies.set(animal.specieId, []);
    bySpecies.get(animal.specieId).push({ animal, name });
  });
  bySpecies.forEach((entries) => {
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        if (entries[i].name === entries[j].name) {
          addPair(entries[i].animal, entries[j].animal, DUPLICATE_MATCH_TYPES.SAME_NAME);
        } else if (namesAreSimilar(entries[i].name, entries[j].name)) {
          addPair(entries[i].animal, entries[j].animal, DUPLICATE_MATCH_TYPES.SIMILAR_NAME);
        }
      }
    }
  });

  // Regroupement des paires en clusters (union-find)
  const parent = new Map();
  const find = (id) => {
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root);
    let current = id;
    while (current !== root) {
      const next = parent.get(current);
      parent.set(current, root);
      current = next;
    }
    return root;
  };
  const union = (idA, idB) => {
    if (!parent.has(idA)) parent.set(idA, idA);
    if (!parent.has(idB)) parent.set(idB, idB);
    const rootA = find(idA);
    const rootB = find(idB);
    if (rootA !== rootB) parent.set(rootB, rootA);
  };

  pairs.forEach((matchTypes, key) => {
    const [idA, idB] = key.split("|");
    union(idA, idB);
  });

  const groupsByRoot = new Map();
  pairs.forEach((matchTypes, key) => {
    const [idA] = key.split("|");
    const root = find(idA);
    if (!groupsByRoot.has(root)) {
      groupsByRoot.set(root, { memberIds: new Set(), matchTypes: new Set() });
    }
    const group = groupsByRoot.get(root);
    key.split("|").forEach((id) => group.memberIds.add(id));
    matchTypes.forEach((type) => group.matchTypes.add(type));
  });

  const groups = [];
  groupsByRoot.forEach((group) => {
    const groupAnimals = [...group.memberIds]
      .map((id) => byId.get(id))
      .filter(Boolean)
      .sort((a, b) => (a.animalName || "").localeCompare(b.animalName || ""));
    if (groupAnimals.length < 2) return;
    const matchTypes = [...group.matchTypes];
    groups.push({
      animals: groupAnimals,
      matchTypes,
      confidence: matchTypes.some((type) => HIGH_CONFIDENCE_MATCHES.includes(type))
        ? "high"
        : "medium",
    });
  });

  // Seuls les groupes à confiance élevée sont remontés (décision 2026-07-03) :
  // les rapprochements par noms proches seuls génèrent trop de faux positifs.
  // Ils restent une preuve secondaire au sein d'un cluster déjà confirmé.
  return groups
    .filter((group) => group.confidence === "high")
    .sort((a, b) =>
      (a.animals[0].animalName || "").localeCompare(b.animals[0].animalName || "")
    );
};

/**
 * Signaux divergents à afficher pour aider l'utilisateur à trancher :
 * deux vrais doublons ont normalement le même sexe et la même naissance.
 */
export const getGroupWarnings = (group) => {
  const definedValues = (getter) =>
    new Set(
      group.animals
        .map(getter)
        .filter((value) => value !== undefined && value !== null && value !== "")
    );

  const sexes = definedValues((a) =>
    a.animalSexe && a.animalSexe.value && a.animalSexe.value !== "Undefined"
      ? a.animalSexe.value
      : null
  );
  // Comparaison sur la forme canonique : "23/04/2020" et "2020-04-23" sont la
  // même date, et deux précisions différentes ne doivent pas se comparer à
  // l'octet près (formatBirthdate renvoie "" sur une valeur illisible).
  const birthdates = definedValues((a) => formatBirthdate(a.animalBirthdate));

  return {
    sexMismatch: sexes.size > 1,
    birthdateMismatch: birthdates.size > 1,
  };
};
